'use client'

import { useEffect, useMemo, useRef, useState, useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { MoreHorizontal, X } from 'lucide-react'

type PhotoItem = {
  url: string
  filename: string
  created_at: string | null
  alt: string | null
}

export default function YourPhotosGrid({
  items,
  isOwner,
  onUpdateAlt,
  onDelete,
}: {
  items: PhotoItem[]
  isOwner: boolean
  onUpdateAlt: (input: { url: string; alt: string }) => Promise<void>
  onDelete: (input: { url: string }) => Promise<void>
}) {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const [openMenuUrl, setOpenMenuUrl] = useState<string | null>(null)
  const [modalUrl, setModalUrl] = useState<string | null>(null)
  const [altDraft, setAltDraft] = useState('')
  const altInputRef = useRef<HTMLInputElement | null>(null)

  const activeItem = useMemo(() => {
    if (!modalUrl) return null
    return items.find(i => i.url === modalUrl) || null
  }, [items, modalUrl])

  useEffect(() => {
    if (!modalUrl) return
    const item = items.find(i => i.url === modalUrl)
    setAltDraft(item?.alt || '')
  }, [items, modalUrl])

  useEffect(() => {
    if (!modalUrl) return
    const t = window.setTimeout(() => {
      altInputRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(t)
  }, [modalUrl])

  useEffect(() => {
    if (!openMenuUrl) return
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) return
      if (target.closest('[data-photo-menu="true"]')) return
      setOpenMenuUrl(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [openMenuUrl])

  const closeModal = () => {
    setModalUrl(null)
    setAltDraft('')
  }

  const openModalFor = (url: string) => {
    setModalUrl(url)
    setOpenMenuUrl(null)
  }

  const handleSave = () => {
    if (!activeItem) return
    startTransition(async () => {
      await onUpdateAlt({ url: activeItem.url, alt: altDraft })
      router.refresh()
      closeModal()
    })
  }

  const handleDelete = () => {
    if (!activeItem) return
    startTransition(async () => {
      await onDelete({ url: activeItem.url })
      router.refresh()
      closeModal()
    })
  }

  const handleQuickDelete = (url: string) => {
    const ok = window.confirm('Delete this image permanently?')
    if (!ok) return
    startTransition(async () => {
      await onDelete({ url })
      router.refresh()
      setOpenMenuUrl(null)
    })
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {items.map(p => (
          <div key={p.url} className="relative rounded-xl border border-[var(--color-border)] bg-[var(--bg-base)]">
            <div className="aspect-square w-full bg-[var(--bg-secondary)] overflow-hidden rounded-xl">
              <img
                src={p.url}
                alt={(p.alt && p.alt.trim().length > 0 ? p.alt : p.filename) || 'Photo'}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            {isOwner && (
              <div className="absolute top-2 right-2" data-photo-menu="true">
                <button
                  type="button"
                  onClick={() => setOpenMenuUrl(prev => (prev === p.url ? null : p.url))}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/70 transition-colors"
                  aria-label="Photo options"
                >
                  <MoreHorizontal size={16} />
                </button>
                {openMenuUrl === p.url && (
                  <div className="absolute right-0 mt-2 w-44 rounded-xl border border-[var(--color-border)] bg-[var(--bg-elevated)] shadow-lg overflow-hidden z-50">
                    <button
                      type="button"
                      onClick={() => openModalFor(p.url)}
                      className="w-full px-4 py-2.5 text-left text-sm text-[var(--color-text-main)] hover:bg-[var(--bg-secondary)] transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleQuickDelete(p.url)}
                      className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-[var(--bg-secondary)] transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {activeItem && modalUrl && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onMouseDown={e => {
            if (e.target === e.currentTarget) closeModal()
          }}
        >
          <div className="w-full max-w-2xl rounded-2xl border border-[var(--color-border)] bg-[var(--bg-elevated)] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
              <div className="text-sm font-semibold text-[var(--color-text-main)]">Photo</div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-[var(--bg-secondary)] transition-colors"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-[var(--color-border)] bg-[var(--bg-base)] overflow-hidden">
                <div className="aspect-square w-full bg-[var(--bg-secondary)] overflow-hidden">
                  <img
                    src={activeItem.url}
                    alt={(activeItem.alt && activeItem.alt.trim().length > 0 ? activeItem.alt : activeItem.filename) || 'Photo'}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-xl border border-[var(--color-border)] bg-[var(--bg-base)] p-3 space-y-2">
                  <div className="text-xs text-[var(--color-text-muted)]">Filename</div>
                  <div className="text-sm text-[var(--color-text-main)] break-all">{activeItem.filename}</div>
                  <div className="text-xs text-[var(--color-text-muted)] pt-2">URL</div>
                  <div className="text-sm text-[var(--color-text-main)] break-all">{activeItem.url}</div>
                  <div className="text-xs text-[var(--color-text-muted)] pt-2">Uploaded</div>
                  <div className="text-sm text-[var(--color-text-main)]">
                    {activeItem.created_at ? new Date(activeItem.created_at).toLocaleDateString('en-ZA') : 'Unknown'}
                  </div>
                </div>

                <div className="rounded-xl border border-[var(--color-border)] bg-[var(--bg-base)] p-3 space-y-2">
                  <div className="text-xs font-semibold text-[var(--color-text-main)]">
                    <label htmlFor={`alt-input-${activeItem.filename}`}>Alt text</label>
                  </div>
                  <input
                    id={`alt-input-${activeItem.filename}`}
                    name="alt"
                    ref={altInputRef}
                    type="text"
                    value={altDraft}
                    onChange={e => setAltDraft(e.target.value)}
                    maxLength={300}
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-main)] focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] outline-none transition-colors"
                    placeholder="Describe the image"
                    disabled={isPending}
                  />
                </div>

                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="btn btn-outline text-xs px-4 py-2 text-red-400 border-red-900/50 hover:bg-red-900/10"
                    disabled={isPending}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="btn btn-primary text-xs px-4 py-2"
                    disabled={isPending}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
